import React, { useState, useEffect, useRef, useMemo } from "react";
import { ChevronDown, PanelLeftOpen, PanelLeftClose } from "lucide-react";
import { X } from "lucide-react";

export default function Navbar({
  html, css, js, setHtml, setCss, setJs, 
  exportAsHtmlFile, exportAsZip, importHtmlFile, importZipFile, setIsLoading,
  isProjectsOpen, setIsProjectsOpen, sectionTab, setSectionTab, panelLayout, setPanelLayout, onClose
}) {
  const [openMenu, setOpenMenu] = useState(null);

  const exportMenuRef = useRef(null);
  const importMenuRef = useRef(null);
  const layoutMenuRef = useRef(null);
  const fileInputHtmlRef = useRef(null);
  const fileInputZipRef = useRef(null);

  const toggleMenu = (menu) => {
    setOpenMenu((prev) => (prev === menu ? null : menu));
  };

  const handleExportHtml = () => {
    exportAsHtmlFile(html, css, js);
    setOpenMenu(null);
  };
  const handleExportZip = () => {
    exportAsZip(html, css, js);
    setOpenMenu(null);
  };

  const onImportHtmlClick = () => {
    fileInputHtmlRef.current?.click();
    setOpenMenu(null);
  };

  const onImportZipClick = () => {
    fileInputZipRef.current?.click();
    setOpenMenu(null);
  };

  const onHtmlFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await handleImportHtml(file);
    e.target.value = "";
  };

  const onZipFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await handleImportZip(file);
    e.target.value = "";
  };

  const handleImportHtml = async (file) => {
    const { html, css, js } = await importHtmlFile(file, setIsLoading);
    setHtml(html);
    setCss(css);
    setJs(js);
  };

  const handleImportZip = async (file) => {
    const { html, css, js } = await importZipFile(file, setIsLoading);
    setHtml(html);
    setCss(css);
    setJs(js);
  };

  const layoutOptions = [
    { 
      value: "three-panel", 
      label: "All Panels", 
      icon: "⚏",
      description: "Questions + Editor + Preview" 
    },
    { 
      value: "questions-editor", 
      label: "Questions & Editor", 
      icon: "◨",
      description: "Focus on learning and coding" 
    },
    { 
      value: "editor-preview", 
      label: "Editor & Preview", 
      icon: "◧",
      description: "Focus on coding and output" 
    }
  ];

  const currentLayout = layoutOptions.find(opt => opt.value === panelLayout);

  const layoutMenu = useMemo(() => (
    <div>
      {layoutOptions.map((option) => (
        <button
          key={option.value}
          className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2"
          onMouseDown={() => {
            setPanelLayout(option.value);
            setOpenMenu(null);
          }}
        >
          <span>{option.icon}</span>
          <div>
            <div className="font-medium text-sm">{option.label}</div>
            <div className="text-xs text-gray-500">{option.description}</div>
          </div>
        </button>
      ))}
    </div>
  ), [setPanelLayout]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        openMenu &&
        !exportMenuRef.current?.contains(event.target) &&
        !importMenuRef.current?.contains(event.target) &&
        !layoutMenuRef.current?.contains(event.target)
      ) {
        setOpenMenu(null);
      }
    }
    if (openMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenu]);

  return (
    <div className="h-14 bg-indigo-500 border-b border-gray-200 px-4 flex items-center justify-between h-[55px]">
      <div className="flex items-center gap-3">
        <button
          onClick={onClose}
          className="bg-indigo-300 text-white px-3 py-1 rounded-full hover:bg-indigo-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
        <button
          onClick={() => setIsProjectsOpen(!isProjectsOpen)}
          className="ml-2 px-1 py-1.5 rounded-md flex items-center gap-1 hover:bg-indigo-400"
        >
          { !isProjectsOpen ? (<PanelLeftOpen size={21} />): (<PanelLeftClose size={21} />) }
        </button>
        <h1 className="text-lg custom-font-title font-semibold">Web Development</h1>
      </div>

      {/* Center - Panel Layout Control */}
      { sectionTab === 1 && (<div className="flex items-center gap-3 text-sm text-gray-500">
        <p className="text-xs text-white my-auto">Layout:</p>
        <div className="relative" ref={layoutMenuRef}>
          <button
            onClick={() => toggleMenu("layout")}
            className="px-2 py-1 bg-white rounded-lg flex items-center cursor-pointer gap-2 hover:bg-gray-200"
            title={currentLayout?.description}
          >
            <span className="text-sm">{currentLayout?.icon}</span>
            <span className="text-xs">{currentLayout?.label}</span>
            <ChevronDown size={16} />
          </button>
          <div className={`absolute top-full left-0 mt-2 w-56 bg-white shadow-lg rounded-lg border z-50 transition-all duration-300
          ${openMenu === "layout"? "max-h-[20rem] opacity-100 overflow-hidden" : "max-h-0 opacity-0 z-[-10]"}`}>
            {layoutMenu}
          </div>
        </div>
      </div>)}

      <div className="flex items-center gap-3 text-sm text-gray-500 flex flex-row">
        <p className="text-xs text-white my-auto">Mode:</p>
        <div className="flex bg-white border-2 border-gray-600 relative overflow-hidden rounded-lg">
          <button
            onClick={() => setSectionTab(1)}
            className={`py-2 px-4 border-r border-gray-600 focus:outline-none transition-colors duration-300 ${
              sectionTab === 1
                ? 'text-indigo-600 font-semibold'
                : ''
            }`}
          >
            Tutorial
          </button>
          <button
            onClick={() => setSectionTab(2)}
            className={`py-2 px-4 border-l border-gray-600 focus:outline-none transition-colors duration-300 ${
              sectionTab === 2
                ? 'text-indigo-600 font-semibold'
                : ''
            }`}
          >
            Creating
          </button>

          {/* Animated background underline */}
          <div
            className="absolute bottom-0 left-0 h-1 bg-indigo-600 transition-all duration-300 ease-in-out"
            style={{
              width: sectionTab === 1 ? '50%' : '50%',
              transform: sectionTab === 1 ? 'translateX(0)' : 'translateX(100%)',
            }}
          ></div>
        </div>
      </div>
      <div className="flex items-center gap-3 text-sm text-gray-500 flex flex-row">
        <div className="flex gap-4">
          {/* Export Menu */}
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => toggleMenu("export")}
              className="px-3 py-1.5 bg-blue-700 text-white rounded-lg flex cursor-pointer items-center gap-1 hover:bg-blue-800"
            >
              Export <ChevronDown size={16} />
            </button>
            <div className={`absolute left-0 mt-2 w-40 bg-white shadow-lg rounded-lg border z-50 
            ${openMenu === "export"? "max-h-[20rem] opacity-100 overflow-hidden" : "max-h-0 opacity-0 z-[-10]"}`}>
              <button 
                className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100"
                onClick={handleExportHtml}
              >
                Export as HTML
              </button>
              <button 
                className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100"
                onClick={handleExportZip}
              >
                Export as ZIP
              </button>
            </div>
          </div>

          {/* Import Menu */}
          <div className="relative" ref={importMenuRef}>
            <button
              onClick={() => toggleMenu("import")}
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg cursor-pointer flex items-center gap-1 hover:bg-green-700"
            >
              Import <ChevronDown size={16} />
            </button>
            <div className={`absolute left-0 mt-2 w-40 bg-white shadow-lg rounded-lg border z-50 
            ${openMenu === "import"? "max-h-[20rem] opacity-100 overflow-hidden" : "max-h-0 opacity-0 z-[-10]"}`}>
              <button 
                className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100"
                onClick={onImportHtmlClick}
              >
                Import HTML
              </button>
              <button 
                className="w-full text-left px-4 py-2 cursor-pointer hover:bg-gray-100"
                onClick={onImportZipClick}
              >
                Import ZIP
              </button>
              <input
                type="file"
                accept=".html"
                ref={fileInputHtmlRef}
                className="hidden"
                onChange={onHtmlFileChange}
              />
              <input
                type="file"
                accept=".zip"
                ref={fileInputZipRef}
                className="hidden"
                onChange={onZipFileChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}